import "../../../../App.scss";
import Helper from "../../../../Common/Helper";
import { appContext } from "../../../../AppContext";
import { User } from "./DataModels";
import { PagedResponse } from "@iconics/core";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, LayoutContainer, LayoutContent, RightButtons, TextField } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { longDebounceTimeInMilliseconds } from "@iconics/core";
import { Debouncer } from "@iconics/core";
import { Guid } from "@iconics/core";

class ListUsers extends IbssPage<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private userCanUpdate: boolean;
    private userCanCreate: boolean;
    private deboucer = new Debouncer(longDebounceTimeInMilliseconds);

    constructor(props: IProps)
    {
        super(props);
        this.userCanCreate = this.local.hasRight("ADMINPORTAL.Users.Create");
        this.userCanUpdate = this.local.hasRight("ADMINPORTAL.Users.Update");

        this.state =
        {
            filterText: '',
            gridData: { rows: [] },
            gridRefreshToken: Guid.new().toString(),
        }
    }

    public componentDidMount(): void
    {
        this.pageTitle = this.labels.Platform_User_PageTitle;
    }

    private handleFilterTextChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.setState({ filterText: event.target.value });
        this.deboucer.run(() => this.setState({ gridRefreshToken: Guid.new().toString() }));
    }

    private async handleRowsRequest(request: IGridRowsRequest<IUser>): Promise<IGridRowsResponse<IUser>>
    {
        const { filterText } = this.state;

        const userResponse = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.get<PagedResponse<User[]>>({
            select: User,
            tenancy: appContext().tenantId,
            skipToken: request.skipToken,
            top: request.take,
            filter: `contains(PreferredName, '${filterText}') or contains(Email, '${filterText}')`,
            orderBy: DataGrid.toODataOrderBy(request.sorter),
        });

        const users = userResponse.value.map(user =>
        ({
            id: user.Id,
            preferredName: user.PreferredName,
            email: user.Email,
            userType: user.UserType,
        } as IUser));

        return { rows: users, nextSkipToken: userResponse.skipToken };
    }

    private navigateToEditUser(id: string): void
    {
        if (this.userCanUpdate)
        {
            this.props.navigate(`/security-users/userdetails/${id}`)
        }
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <Card
                        icon={<SerendieSymbol name='user' />}
                        title={this.labels.Platform_User_ListTitle}
                        subTitle={this.labels.Platform_User_ListSubTitle}
                    >
                        <GridContainer>
                            <GridItem expanded={3}>
                                <TextField
                                    leftContent={<SerendieSymbol name={"magnifying-glass"} />}
                                    placeholder={this.labels.Platform_CommonAction_Search}
                                    value={this.state.filterText}
                                    onChange={e => this.handleFilterTextChange(e)}
                                />
                            </GridItem>
                            <GridItem expanded={9}>
                                <ButtonBar>
                                    <RightButtons>
                                        <Button
                                            size='small'
                                            styleType='ghost'
                                            leftIcon={<SerendieSymbol name='plus' />}
                                            onClick={() => this.props.navigate(`/security-users/userdetails/0`)}
                                            disabled={!this.userCanCreate}
                                        >
                                            {this.labels.Platform_CommonAction_Add}
                                        </Button>
                                    </RightButtons>
                                </ButtonBar>
                            </GridItem>
                            <GridItem>
                                <DataGrid
                                    columns={[
                                        {
                                            columnId: Helper.nameOf<User>('PreferredName'),
                                            title: this.labels.User_PreferredName,
                                            getCellValue: i => i.preferredName,
                                            columnWidth: 25,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<User>('Email'),
                                            title: this.labels.User_Email,
                                            getCellValue: i => i.email,
                                            columnWidth: 35,
                                            sortable: true,
                                        },
                                        {
                                            columnId: Helper.nameOf<User>('UserType'),
                                            title: this.labels.User_UserType,
                                            getCellValue: i => i.userType,
                                            columnWidth: 15,
                                            sortable: true,
                                        },
                                    ]}
                                    data={this.state.gridData}
                                    onChange={i => this.setState({ gridData: i })}
                                    rowId={i => i.id}
                                    onRowsRequest={i => this.handleRowsRequest(i)}
                                    onCellClicked={i => this.navigateToEditUser(i.id)}
                                    refreshToken={this.state.gridRefreshToken}
                                />
                            </GridItem>
                        </GridContainer>
                    </Card>
                </LayoutContent>
            </LayoutContainer>
        );
    }
}

export default withRouting(ListUsers);

interface IState
{
    filterText: string;
    gridData: IGridData<IUser>;
    gridRefreshToken: string;
}

export interface IUser
{
    id: string;
    preferredName: string;
    email: string;
    userType: string;
}

interface IProps extends IRouterProps { }
